import { bothChoices, type BetChoice } from '@/lib/bet-choices'
import { cn } from '@/lib/utils'

interface ChoiceSelectProps {
  id?: string
  choices: BetChoice[]
  /** The field's text: '', one value ('high'), or several space-separated ('high low'). */
  value: string
  onChange: (value: string) => void
  /** Offer ทั้งสอง for fields that take a list. Off for fields that take exactly one. */
  allowBoth?: boolean
  invalid?: boolean
  'aria-label'?: string
  className?: string
}

/**
 * A pick-one list standing in for the text box on สูง/ต่ำ and คู่/คี่.
 *
 * Native <select> rather than the Radix one: the other pickers on these screens are native
 * (the หวย picker, the history filters), it is the right control on a phone, and it keeps
 * the field a real form control the keyboard and a screen reader already understand.
 *
 * It speaks the same string the text box did. Choosing ทั้งสอง writes 'high low' — the very
 * text a person would have typed — so the parser, the validator and the request body
 * downstream cannot tell the two apart and needed no change.
 */
export function ChoiceSelect({
  id, choices, value, onChange, allowBoth = false, invalid, className, ...rest
}: ChoiceSelectProps) {
  const both = bothChoices(choices)
  // A value the list does not offer (a half-typed leftover from another bet type, or an
  // edit that loaded something unexpected) shows as the empty prompt rather than as a
  // blank, unselectable box.
  const offered = value === both ? allowBoth : choices.some((c) => c.value === value)
  const shown = offered ? value : ''

  return (
    <select
      id={id}
      value={shown}
      onChange={(e) => onChange(e.target.value)}
      aria-invalid={invalid}
      aria-label={rest['aria-label']}
      className={cn(
        'block h-9 w-full rounded-[var(--radius-sm)] border border-input bg-background px-2.5 pr-8',
        'font-mono text-sm text-foreground',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        'aria-[invalid=true]:border-destructive',
        // The prompt reads as a placeholder, not as a chosen value.
        shown === '' && 'text-muted-foreground',
        className,
      )}
    >
      <option value="">เลือก…</option>
      {choices.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
      {allowBoth && <option value={both}>ทั้งสอง ({choices.map((c) => c.label).join(' + ')})</option>}
    </select>
  )
}
